import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useBackupController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useBackupController';
import {
	DEAL_ROUTES,
	MIP_ROUTES,
	ROUTES,
	MIP_MANAGE_ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { ProgrammeStatus, GetMipDraftStepStatusRes } from '@dealstack/shared/lib/generated';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { Step } from '@dealstack/frontend/shared/components/SubstepMenu/step.types';
import { useSubstepMenu } from '@dealstack/frontend/shared/components/SubstepMenu/useSubstepMenu';
import * as Sentry from '@sentry/react';
import {
	SAVE_INVALID,
	useWizardStepRegistry,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type {
	WizardNavigationIntent,
	WizardSaveResult,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import {
	deleteMipDraft,
	getMIPDetailsDataForJoiner,
	getMIPDraftStepsStatuses,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getDealBasicData } from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';
import {
	useTranslationCommonUi,
	useTranslationDefault,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import {
	MIP_DRAFT_STEPS,
	MIP_DRAFT_STEPS_STATUS,
} from '@dealstack/shared/lib/common/business/mipDraft';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { isNotEmpty } from '@dealstack/shared/lib/isNotEmpty.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { useMutation } from '@tanstack/react-query';
import { isEqual, isNumber } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, resolvePath, useLocation, useNavigate } from 'react-router-dom';
import { ISideListConfigEntry } from '@dealstack/frontend/shared/components/listOld/PrimarySideList/PrimarySideList.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';

export enum MIP_CREATE_ROUTES {
	GENERAL = `/general`,
	PERSONAL = '/personal',
	SECURITIES = '/securities',
	KYC = '/kyc',
	CONTRACTS = '/contracts',
	SCHEDULE = '/schedule',
	INVESTORS = '/investors',
	COLLABORATE = '/collaborate',
	SUMMARY = '/summary',
}

export const MIP_CREATE_SUB_ROUTES = Object.freeze({
	KYC: {
		MAIN_ROUTE: MIP_CREATE_ROUTES.KYC,
		KYC_DOCUMENTS: '/documents',
		KYC_DECLARATIONS: '/declarations',
	},
});

export interface MipCreateSidebarConfigStep {
	title: string;
	route: MIP_CREATE_ROUTES;
	step: GetMipDraftStepStatusRes['step'];
	subSteps?: Step[];
	key: number;
	state?: ISideListConfigEntry['state'];
	stateMessage?: string;
	stateCode?: string | number;
}

const getSideBarConfig = (
	t: TranslateFunction<I18Namespaces.MIP_CREATE>
): MipCreateSidebarConfigStep[] => {
	const steps = [
		{
			title: t('sidebar.section_list.general_deal'),
			route: MIP_CREATE_ROUTES.GENERAL,
			step: MIP_DRAFT_STEPS.GENERAL,
			key: 0,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_GENERAL,
		},
		{
			title: t('sidebar.section_list.securities'),
			route: MIP_CREATE_ROUTES.SECURITIES,
			step: MIP_DRAFT_STEPS.SECURITIES,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_SECURITIES,
		},
		{
			title: t('sidebar.section_list.personal_data'),
			route: MIP_CREATE_ROUTES.PERSONAL,
			step: MIP_DRAFT_STEPS.PERSONAL,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_PERSONAL,
			key: 1,
		},

		{
			title: t('sidebar.section_list.kyc'),
			route: MIP_CREATE_ROUTES.KYC,
			step: MIP_DRAFT_STEPS.KYC,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_KYC,
			key: 2,
			subSteps: [
				{
					name: t('sidebar.section_list.kyc_documents'),
					routeToNavigate: `.${MIP_CREATE_SUB_ROUTES.KYC.MAIN_ROUTE}${MIP_CREATE_SUB_ROUTES.KYC.KYC_DOCUMENTS}`,
					routeToMatch: MIP_CREATE_SUB_ROUTES.KYC.KYC_DOCUMENTS,
					id: MIP_CREATE_SUB_ROUTES.KYC.KYC_DOCUMENTS,
				},
				{
					name: t('sidebar.section_list.kyc_declarations'),
					routeToNavigate: `.${MIP_CREATE_SUB_ROUTES.KYC.MAIN_ROUTE}${MIP_CREATE_SUB_ROUTES.KYC.KYC_DECLARATIONS}`,
					routeToMatch: MIP_CREATE_SUB_ROUTES.KYC.KYC_DECLARATIONS,
					id: MIP_CREATE_SUB_ROUTES.KYC.KYC_DECLARATIONS,
				},
			],
		},
		{
			title: t('sidebar.section_list.contracts'),
			route: MIP_CREATE_ROUTES.CONTRACTS,
			step: MIP_DRAFT_STEPS.CONTRACTS,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_CONTRACTS,
			key: 3,
		},
		{
			title: t('sidebar.section_list.schedule'),
			route: MIP_CREATE_ROUTES.SCHEDULE,
			step: MIP_DRAFT_STEPS.SCHEDULE,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_SCHEDULE,
			key: 4,
		},
		{
			title: t('sidebar.section_list.investors'),
			route: MIP_CREATE_ROUTES.INVESTORS,
			step: MIP_DRAFT_STEPS.INVESTORS,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_INVESTORS,
			key: 5,
		},
		{
			title: t('sidebar.section_list.collaborate'),
			route: MIP_CREATE_ROUTES.COLLABORATE,
			step: MIP_DRAFT_STEPS.COLLABORATE,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_COLLABORATE,
			key: 6,
		},
		{
			title: t('sidebar.section_list.finalise'),
			route: MIP_CREATE_ROUTES.SUMMARY,
			step: MIP_DRAFT_STEPS.SUMMARY,
			dataTestId: TEST_ELEMENT_IDS.MIP_SIDEBAR_STEP_SUMMARY,
			key: 7,
		},
	];

	return steps.map((step, index) => ({
		...step,
		key: index,
	}));
};

const STEP_STATUS = {
	warning: 'warning',
	error: 'error',
};
const MAPPED_STEP_STATUS = {
	[MIP_DRAFT_STEPS_STATUS.ERROR_INVALID_DATA]: STEP_STATUS.error,
	[MIP_DRAFT_STEPS_STATUS.WARNING]: STEP_STATUS.warning,
	[MIP_DRAFT_STEPS_STATUS.ERROR_MISSING_DATA]: STEP_STATUS.error,
	[MIP_DRAFT_STEPS_STATUS.ERROR_MAX_LENGTH]: STEP_STATUS.error,
};

// A failed request still moves on; only an invalid step keeps the user where they are.
const shouldNavigateAfter = (result: WizardSaveResult) =>
	result.ok || result.reason === 'request-failed';

export const useMIPCreateController = () => {
	const { mipId, dealId } = useRequiredParams();
	const location = useLocation();
	const navigate = useNavigate();
	// Step links are relative to the create flow, not to the step currently open.
	const mipCreatePath = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}`, {
		dealId,
		mipId,
	});
	const tMipCreate = useTranslationMipCreate();
	const tDefault = useTranslationDefault();
	const tCommonUi = useTranslationCommonUi();
	const { deals, deal } = useContext(DealContext) || {};
	const stepRegistry = useWizardStepRegistry();

	const getMipRequest = useWrappedRequest(getMIPDetailsDataForJoiner);
	const getMipDraftStepsStatusesRequest = useWrappedRequest(getMIPDraftStepsStatuses);
	const getProjectBasicDataRequest = useWrappedRequest(getDealBasicData);
	const deleteMipDraftRequest = useMutation({
		mutationFn: deleteMipDraft,
	});

	const [isLoading, setIsLoading] = useState(false);
	const [disableContinueButton, setDisableContinueButton] = useState(false);
	const [popoverMessage, setPopoverMessage] = useState('');
	const [visitedSteps, setVisitedSteps] = useState<GetMipDraftStepStatusRes['step'][]>([]);

	useBreadcrumbs(
		[
			...makeDealPageBreadcrumbs({
				tCommonUi,
				dealId,
				dealName: getProjectBasicDataRequest.response?.payload?.name || deal?.name,
				deals,
			}),
			{
				name:
					getMipRequest?.response?.payload?.programmeName ||
					tCommonUi('breadcrumbs.untitled_mip'),
				url: generatePath(
					`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}${MIP_CREATE_ROUTES.GENERAL}`,
					{
						dealId: dealId ?? '',
						mipId: mipId ?? '',
					}
				),
			},
		],
		{ skipFirst: true }
	);

	const refreshData = () => {
		getMipRequest.fetch();
		getMipDraftStepsStatusesRequest.fetch();
	};

	useEffect(() => {
		refreshData();
		getProjectBasicDataRequest.fetch();
	}, []);

	useEffect(() => {
		if (
			!getMipRequest.response?.payload?.state ||
			getMipRequest.response?.payload?.state === 'DRAFT'
		) {
			return;
		}
		const investorsPath = mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, { dealId, mipId });
		// Land on the investors tab with the Joiners quick-filter pre-applied
		const path = `${investorsPath}?quickFilters=${encodeURIComponent(
			JSON.stringify({ programmeStatuses: ProgrammeStatus.JOINER })
		)}`;
		navigate(path);
	}, [getMipRequest.response?.payload]);

	const stepsStatuses = useMemo(
		() => getMipDraftStepsStatusesRequest.response?.payload,
		[getMipDraftStepsStatusesRequest.response?.payload]
	);

	const sideBarConfig = useMemo(() => {
		const config = getSideBarConfig(tMipCreate);
		if (!stepsStatuses) {
			return config;
		}

		const sidebarConfigWithStatuses = config.map((sidebarStep) => {
			const sidebarStepStatus = stepsStatuses.find((step) => step.step === sidebarStep.step);
			if (!sidebarStepStatus) {
				return sidebarStep;
			}

			const stepState: ISideListConfigEntry['state'] = (() => {
				if (!visitedSteps.find((visitedStep) => visitedStep === sidebarStepStatus.step)) {
					return undefined;
				}
				return MAPPED_STEP_STATUS[sidebarStepStatus.status];
			})();

			return {
				...sidebarStep,
				state: stepState,
				stateMessage: stepState ? tDefault(`error_codes.${sidebarStepStatus.code}`) : '',
				stateCode: stepState ? sidebarStepStatus.code : '',
			};
		});

		return sidebarConfigWithStatuses;
	}, [stepsStatuses, visitedSteps]);

	const activeStep = useMemo(
		() => sideBarConfig.find((entry) => location.pathname.includes(entry.route)),
		[location, sideBarConfig]
	);

	const activeSidebarKey = useMemo(() => activeStep?.key, [activeStep]);

	const nextSectionLink = useMemo(() => {
		if (!isNumber(activeSidebarKey)) {
			return;
		}
		const nextLinkKey = activeSidebarKey + 1;
		const nextLink = sideBarConfig.find((item) => item.key === nextLinkKey);
		return nextLink?.route;
	}, [sideBarConfig, activeSidebarKey]);

	const prevSectionLink = useMemo(() => {
		if (!isNumber(activeSidebarKey)) {
			return;
		}
		const nextLinkKey = activeSidebarKey - 1;
		const nextLink = sideBarConfig.find((item) => item.key === nextLinkKey);
		return nextLink?.route;
	}, [sideBarConfig, activeSidebarKey]);

	const {
		steps: activeStepSubSteps,
		currentStep: currentActiveSubStep,
		nextStep: nextSubStep,
		resetSubstepsState,
	} = useSubstepMenu({
		initialSteps: activeStep?.subSteps ?? [],
		useRoutesLogic: true,
		routesBasePath: mipCreatePath,
	});

	useEffect(() => {
		const isSubstepsEqual = activeStep?.subSteps?.every((newSubStep) =>
			activeStepSubSteps.some((oldSubStep) => oldSubStep.id === newSubStep.id)
		);
		if (isSubstepsEqual) {
			return;
		}

		resetSubstepsState(activeStep?.subSteps ?? []);
	}, [activeStep?.subSteps]);

	const addVisitedStep = () => {
		if (!activeStep) {
			return;
		}

		setVisitedSteps((prevState) => Array.from(new Set([...prevState, activeStep.step])));
	};

	const saveActiveStep = async (intent: WizardNavigationIntent) => {
		if (!stepRegistry.hasActiveStep()) {
			// A click can land before the step's registration; stay put rather than crash the wizard.
			Sentry.captureException(
				new Error(`MIP create: no step registered to handle "${intent}"`)
			);
			return SAVE_INVALID;
		}
		const result = await stepRegistry.saveActiveStep(intent);
		if (result.ok && !result.skipped) {
			showToastV2({
				title: tMipCreate('common.toast.success.message'),
			});
			getMipDraftStepsStatusesRequest.fetch();
		}
		return result;
	};

	const onSaveDraft = async () => {
		await saveActiveStep('save');
	};

	const conditionallyNavigate = async (link: string, intent: WizardNavigationIntent = 'jump') => {
		const result = await saveActiveStep(intent);
		if (!shouldNavigateAfter(result)) {
			return;
		}
		addVisitedStep();
		navigate(resolvePath(link, mipCreatePath));
	};

	const onPrevSection = async () => {
		await conditionallyNavigate(`.${prevSectionLink}`, 'back');
	};

	const onNextSection = async () => {
		await conditionallyNavigate(nextSubStep?.routeToNavigate ?? `.${nextSectionLink}`, 'next');
	};

	const onSidebarLinkClick = async (key: number | string) => {
		const section = sideBarConfig.find((item) => item.key === key);
		await conditionallyNavigate(`.${section?.route}`, 'jump');
	};

	const { dataBackup, updateBackupData, removeBackupData } = useBackupController();

	useEffect(() => {
		const steps = StorageUtilities.getItem<GetMipDraftStepStatusRes['step'][]>(
			StorageKeys.VISITED_STEPS_STORAGE_KEY,
			mipId
		);
		if (!steps) {
			return;
		}
	}, []);

	useEffect(() => {
		if (!visitedSteps) {
			return;
		}
		StorageUtilities.setItem(StorageKeys.VISITED_STEPS_STORAGE_KEY, visitedSteps, mipId);
	}, [visitedSteps]);

	useEffect(() => {
		const allSteps = Object.values(MIP_DRAFT_STEPS);
		if (
			!activeStep ||
			activeStep.step !== MIP_DRAFT_STEPS.SUMMARY ||
			isEqual(allSteps, visitedSteps)
		) {
			return;
		}

		setVisitedSteps(Object.values(MIP_DRAFT_STEPS));
	}, [activeStep]);

	const refreshStatuses = useCallback(() => {
		getMipDraftStepsStatusesRequest.fetch();
	}, [getMipDraftStepsStatusesRequest]);

	return {
		refreshData,
		getMipRequest,
		stepRegistry,
		isSavelessStep: !stepRegistry.activeStepPersists,
		onSaveDraft,
		onSidebarLinkClick,
		conditionallyNavigate,
		sideBarConfig,
		activeSidebarKey,
		nextSectionLink,
		prevSectionLink,
		onPrevSection,
		onNextSection,
		isLoading,
		setIsLoading,
		dataBackup,
		updateBackupData,
		removeBackupData,
		mipId,
		dealId,
		disableContinueButton,
		setDisableContinueButton,
		popoverMessage,
		setPopoverMessage,
		activeStep,
		stepsStatuses,
		getMipDraftStepsStatusesRequest,
		showSubStepsNavigation: isNotEmpty(activeStep?.subSteps),
		hideBottomNavigation:
			isNotEmpty(activeStep?.subSteps) || activeStep?.step === MIP_DRAFT_STEPS.INVESTORS,
		activeStepSubSteps,
		currentActiveSubStep,
		refreshStatuses,
		deleteMipDraftRequest,
	};
};
